import React, { useState } from 'react';
import { apiService } from '../services/api';
import { useApi, useMutation } from '../hooks/useApi';
import logger from '../utils/logger';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { Truck, PlusCircle, Pencil, Trash2, AlertCircle } from 'lucide-react';

function Furnizuesit() {
  const { settings } = useSettings();
  const [form, setForm] = useState({
    emri: '',
    adresa: '',
    telefoni: '',
    email: '',
  });
  const [editId, setEditId] = useState(null);

  const { data: furnizuesitData, execute: refetchFurnizuesit } = useApi(
    () => apiService.get('/furnizuesit'),
    [],
    { immediate: true, showNotifications: false }
  );

  const furnizuesit = Array.isArray(furnizuesitData) ? furnizuesitData : (furnizuesitData?.data || []);

  const { mutate: saveFurnizues, loading: saving } = useMutation(
    async (data) => {
      if (editId) {
        return await apiService.put(`/furnizuesit/${editId}`, data);
      } else {
        return await apiService.post('/furnizuesit', data);
      }
    },
    {
      successMessage: editId ? 'Furnizuesi u përditësua me sukses!' : 'Furnizuesi u shtua me sukses!',
      onSuccess: () => {
        setForm({ emri: '', adresa: '', telefoni: '', email: '' });
        setEditId(null);
        refetchFurnizuesit();
      }
    }
  );

  const { mutate: deleteFurnizues } = useMutation(
    async (id) => {
      return await apiService.delete(`/furnizuesit/${id}`);
    },
    {
      successMessage: 'Furnizuesi u fshi me sukses!',
      onSuccess: () => {
        refetchFurnizuesit();
      }
    }
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    saveFurnizues(form);
  };

  const handleEdit = (f) => {
    setEditId(f.id);
    setForm({
      emri: f.emri || '',
      adresa: f.adresa || '',
      telefoni: f.telefoni || '',
      email: f.email || '',
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('A deshironi te fshini kete furnizues?')) return;
    deleteFurnizues(id);
  };

  // Nëse furnizuesit janë të çaktivizuar, shfaq mesazh
  if (settings && settings.furnizuesit_aktiv === false) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center card-premium p-8 max-w-md">
          <AlertCircle className="mx-auto mb-4 text-orange-500" size={48} />
          <h2 className="text-2xl font-bold text-gray-700 dark:text-gray-300 mb-4">
            Furnizuesit janë të çaktivizuar
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Ky feature është i çaktivizuar në cilësimet e sistemit. Për ta aktivizuar, shkoni te Cilësimet e Sistemit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full animate-fade-in">
      <h2 className="text-3xl md:text-4xl font-bold mb-8 text-gradient-logo flex items-center gap-3 animate-slide-down">
        <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
          <Truck size={32} className="text-primary-600 dark:text-primary-400" />
        </div>
        Furnizuesit
      </h2>

      <form onSubmit={handleSubmit} className="card-premium p-6 mb-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <input
          type="text"
          value={form.emri}
          onChange={(e) => setForm({ ...form, emri: e.target.value })}
          placeholder="Emri i furnizuesit"
          className="input-premium"
          required
        />
        <input
          type="text"
          value={form.telefoni}
          onChange={(e) => setForm({ ...form, telefoni: e.target.value })}
          placeholder="Telefoni"
          className="input-premium"
        />
        <input
          type="text"
          value={form.adresa}
          onChange={(e) => setForm({ ...form, adresa: e.target.value })}
          placeholder="Adresa"
          className="input-premium"
        />
        <input
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
          placeholder="Email"
          className="input-premium"
        />
        <button
          type="submit"
          disabled={saving}
          className="md:col-span-2 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <PlusCircle size={18} />
          {saving ? 'Po ruhet...' : (editId ? 'Perditeso' : 'Shto')}
        </button>
      </form>

      <div className="card-premium overflow-hidden">
        <table className="table-premium">
          <thead>
            <tr>
              <th>#</th>
              <th>Emri</th>
              <th>Telefoni</th>
              <th>Email</th>
              <th>Adresa</th>
              <th>Veprime</th>
            </tr>
          </thead>
          <tbody>
            {furnizuesit.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Nuk ka furnizues të regjistruar
                </td>
              </tr>
            ) : (
              furnizuesit.map((f, i) => (
                <tr key={f.id}>
                  <td>{i + 1}</td>
                  <td className="font-medium">{f.emri}</td>
                  <td>{f.telefoni || '-'}</td>
                  <td>{f.email || '-'}</td>
                  <td>{f.adresa || '-'}</td>
                  <td className="space-x-2">
                    <button onClick={() => handleEdit(f)} className="text-secondary-600 dark:text-secondary-400 hover:text-secondary-800 dark:hover:text-secondary-300 inline-flex items-center gap-1 transition-colors">
                      <Pencil size={16} /> Edito
                    </button>
                    <button onClick={() => handleDelete(f.id)} className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 inline-flex items-center gap-1 transition-colors">
                      <Trash2 size={16} /> Fshi
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Furnizuesit;
